import { isPlatformBrowser } from '@angular/common';
import type {
  AfterContentInit,
  AfterViewInit,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
} from '@angular/core';
import {
  ChangeDetectionStrategy,
  Component,
  ContentChild,
  ElementRef,
  EventEmitter,
  HostListener,
  inject,
  Input,
  Output,
  PLATFORM_ID,
  Renderer2,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { ControlValueAccessorDirective } from '@freelancer/directives';
import { ScreenSizeService } from '@freelancer/screen-size';
import { Tracking } from '@freelancer/tracking';
import {
  CalloutComponent,
  CalloutPlacement,
  CalloutSize,
  CalloutViewportFit,
} from '@freelancer/ui/callout';
import { ListItemPadding } from '@freelancer/ui/list-item';
import { TextareaComponent } from '@freelancer/ui/textarea';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import type { Observable } from 'rxjs';
import {
  Subscription,
  BehaviorSubject,
  firstValueFrom,
  of,
  fromEvent,
} from 'rxjs';
import { startWith, withLatestFrom } from 'rxjs/operators';
import { AutoCompleteRichTextComponent } from './auto-complete-rich-text/auto-complete-rich-text.component';
import type {
  AdjustSuggestionOffsetsParams,
  AutoCompleteObject,
  FilterSuggestionsParams,
  PasteObject,
  Selection,
} from './auto-complete.types';

@Component({
  standalone: false,
  selector: 'fl-auto-complete-suggestion-template',
  template: ` <ng-content></ng-content> `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AutoCompleteSuggestionTemplateComponent {}

@Component({
  standalone: false,
  selector: 'fl-auto-complete-input',
  template: ` <ng-content></ng-content> `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AutoCompleteInputComponent implements AfterContentInit, OnDestroy {
  @Output() onInputBlur = new EventEmitter<void>();

  @ContentChild('textarea', { read: ElementRef })
  textarea?: ElementRef<HTMLTextAreaElement>;
  @ContentChild(TextareaComponent) textareaComponent?: TextareaComponent;

  private subscription = new Subscription();

  ngAfterContentInit(): void {
    if (this.textareaComponent) {
      this.subscription.add(
        this.textareaComponent?.inputBlur.subscribe(() => {
          this.onInputBlur.emit();
        }),
      );
    } else if (this.textarea?.nativeElement) {
      this.subscription.add(
        fromEvent(this.textarea.nativeElement, 'blur').subscribe(() => {
          this.onInputBlur.emit();
        }),
      );
    }
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }
}

@UntilDestroy({ className: 'AutoCompleteComponent' })
@Component({
  standalone: false,
  selector: 'fl-auto-complete',
  template: `
    <fl-auto-complete-rich-text
      #autoCompleteRichText
      [control]="control"
      [disableKeys]="disableKeys"
      [selectedSuggestions]="selectedSuggestions"
      (pasteEvent)="handlePasteEvent($event)"
    >
      <ng-content select="fl-auto-complete-input"></ng-content>
    </fl-auto-complete-rich-text>

    <fl-callout
      #suggestionsCallout
      [edgeToEdge]="true"
      [hideCloseButton]="true"
      [mobileCloseButton]="false"
      [mobileDropdown]="true"
      [placement]="suggestionsCalloutPlacement"
      [repositionOnContentResize]="false"
      [size]="(isMobile$ | flAsync) ? undefined : suggestionsCalloutSize"
      [viewportFit]="suggestionsCalloutViewportFit"
      (calloutOpen)="handleOpenCloseEvent(true)"
      (calloutClose)="handleOpenCloseEvent(false)"
    >
      <fl-callout-trigger
        #suggestionsTrigger
        class="CalloutTrigger"
      ></fl-callout-trigger>

      <fl-callout-content #suggestionsCalloutContent>
        @if ((isOpen$ | flAsync) === true) {
          @if (suggestions$ | flAsync; as suggestions) {
            @if (suggestions.length > 0) {
              <fl-list
                class="SuggestionsList"
                [bodyEdgeToEdge]="true"
                [clickable]="true"
                [padding]="ListItemPadding.XXSMALL"
                [selectByKeyboard]="true"
                [bordered]="false"
                (mouseenter)="updateSuggestionHover(true)"
                (mouseleave)="updateSuggestionHover(false)"
              >
                @for (
                  suggestion of suggestions;
                  track trackByOffset($index, suggestion);
                  let isLast = $last
                ) {
                  <fl-list-item
                    [control]="selectedSuggestionControl"
                    [radioValue]="suggestion"
                  >
                    @if (suggestion.completion) {
                      <ng-container
                        [ngTemplateOutlet]="suggestionTemplate"
                        [ngTemplateOutletContext]="{ $implicit: suggestion }"
                      ></ng-container>
                    } @else {
                      <fl-loading-text [rows]="2"></fl-loading-text>
                    }
                  </fl-list-item>
                }
              </fl-list>
            }
          } @else {
            <fl-loading-text
              class="Padded"
              [rows]="2"
            ></fl-loading-text>
          }
        }
      </fl-callout-content>
    </fl-callout>
  `,
  styleUrls: ['./auto-complete.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AutoCompleteComponent
  implements OnChanges, OnDestroy, OnInit, AfterViewInit
{
  ListItemPadding = ListItemPadding;

  readonly suggestionsCalloutViewportFit: CalloutViewportFit = {
    enabled: false,
  };

  /**
   * It makes possible to edit already existing list of AutoCompleteObject.
   */
  @Input() highlightedTextSegments: readonly AutoCompleteObject[] = [];

  @Input() control: FormControl<string>;
  @Input() suggestions?: readonly AutoCompleteObject[];
  @Input() suggestionsCalloutSize = CalloutSize.MEDIUM;
  @Input() suggestionsCalloutPlacement = CalloutPlacement.BOTTOM_LEFT;
  @Input() trackingSection?: string;

  @Output() suggestionsUpdated = new EventEmitter<
    readonly AutoCompleteObject[]
  >();

  @Output() suggestionsOpen = new EventEmitter<void>();
  @Output() suggestionsClose = new EventEmitter<void>();

  @ContentChild(TemplateRef)
  suggestionTemplate: TemplateRef<AutoCompleteSuggestionTemplateComponent>;

  @ViewChild('suggestionsCallout')
  suggestionsCallout: CalloutComponent;

  @ViewChild('suggestionsTrigger', { read: ElementRef })
  suggestionsTrigger: ElementRef<HTMLElement>;

  @ViewChild('autoCompleteRichText')
  autoCompleteRichText: AutoCompleteRichTextComponent;

  @ViewChild('autoCompleteRichText', { read: ElementRef })
  autoCompleteRichTextElementRef: ElementRef<HTMLElement>;

  @ViewChild('suggestionsCalloutContent', { read: ElementRef })
  suggestionsCalloutContent: ElementRef<HTMLElement>;

  // TODO: T267853 - Remove it after raw textarea usage
  // ideally after T180520
  @ContentChild(ControlValueAccessorDirective)
  controlValueAccessorDirective?: ControlValueAccessorDirective;

  @ContentChild(TextareaComponent)
  textareaComponent?: TextareaComponent;

  @ContentChild(AutoCompleteInputComponent)
  autoCompleteInputComponent?: AutoCompleteInputComponent;

  selectedSuggestionControl = new FormControl<AutoCompleteObject | undefined>(
    undefined,
    { nonNullable: true },
  );
  disableKeys = false;
  suggestionHover = false;

  isMobile$: Observable<boolean>;

  private suggestionsSubject$ = new BehaviorSubject<
    readonly AutoCompleteObject[] | undefined
  >(undefined);
  suggestions$ = this.suggestionsSubject$.asObservable();

  private subscription = new Subscription();

  private isOpenSubject$ = new BehaviorSubject<boolean>(false);
  isOpen$ = this.isOpenSubject$.asObservable();

  selectedSuggestions: readonly AutoCompleteObject[] = [];
  private previousText = '';

  private selectionAfterPaste?: Selection;

  /**
   * Tracks whether the current control value change is from a programmatic source.
   */
  private programmaticChangeSubject$ = new BehaviorSubject<boolean>(false);
  private programmaticChange$ = this.programmaticChangeSubject$.asObservable();

  private platformId: object = inject(PLATFORM_ID);
  private tracking = inject(Tracking);
  private renderer = inject(Renderer2);
  private screenSize = inject(ScreenSizeService);

  ngAfterViewInit(): void {
    this.subscription.add(
      this.selectedSuggestionControl.valueChanges.subscribe(
        highlightedSegment => {
          if (highlightedSegment && highlightedSegment.completion) {
            this.closeSuggestions();
            this.insertHighlightedSegment(highlightedSegment);

            this.tracking.trackCustomEvent(
              'SelectedSuggestion',
              this.trackingSection,
              {
                reference_id: highlightedSegment.referenceId,
                reference_type: highlightedSegment.referenceType,
              },
            );
          }
        },
      ),
    );

    this.subscription.add(
      this.control.valueChanges
        .pipe(
          startWith(this.control.value),
          withLatestFrom(this.programmaticChange$),
        )
        .subscribe(([content, isProgrammatic]) => {
          // Only run filtering/offset adjustments for user input, not programmatic changes
          if (!isProgrammatic) {
            if (content?.length > 0 && !this.selectionAfterPaste) {
              const diffLength = content.length - this.previousText.length;
              const caretPosition =
                this.autoCompleteRichText.getSelectionLeftPosition();
              const previousCaretPosition = caretPosition - diffLength;

              // Ensure correct start and end positions for both insertions and deletions
              const startPos = Math.min(previousCaretPosition, caretPosition);
              const endPos = Math.max(previousCaretPosition, caretPosition);

              // Filter out suggestions that were affected by this edit
              this.filterNonOverlappingSuggestions({
                startPos,
                endPos,
              });

              // Maintaining the offsets of the selected suggestions
              if (diffLength !== 0) {
                // For user input, adjust offsets for suggestions after the edit point
                this.adjustSuggestionOffsets({
                  shiftPoint: startPos, // Start adjusting from where edit began
                  shiftDelta: diffLength, // Shift by the difference in content length
                });
              }
            } else if (!this.selectionAfterPaste) {
              this.selectedSuggestions = [];
            }
          }

          this.selectionAfterPaste = undefined;
          this.programmaticChangeSubject$.next(false);
          this.suggestionsUpdated.emit(this.selectedSuggestions);
          this.previousText = content ?? '';
        }),
    );

    this.subscription.add(
      this.autoCompleteInputComponent?.onInputBlur.subscribe(event => {
        if (!this.suggestionHover) {
          // Auto-complete closes automatically when a suggestion is selected
          // Closes suggestions when the cause of blur is outside the auto complete callout
          this.closeSuggestions();
        }
      }),
    );
  }

  ngOnChanges(changes: SimpleChanges): void {
    if ('highlightedTextSegments' in changes && this.highlightedTextSegments) {
      if (this.highlightedTextSegments.length > 0) {
        this.previousText = this.control.value;
        if (this.autoCompleteRichText) {
          this.updateCaretPosition(this.previousText.length);
        }
      }

      this.addHighlightedTextSegments(this.highlightedTextSegments);
    }

    if ('suggestions' in changes) {
      this.suggestionsSubject$.next(this.suggestions);
    }
  }

  ngOnInit(): void {
    this.isMobile$ = this.isMobileViewport();
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
    this.programmaticChangeSubject$.complete();
  }

  trackByOffset(index: number, textSegment: AutoCompleteObject): number {
    return textSegment.offset;
  }

  handleOpenCloseEvent(open: boolean): void {
    this.disableKeys = open;
    this.isOpenSubject$.next(open);
  }

  @HostListener('document:keyup', ['$event'])
  handleKeyboardEvent(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      this.closeSuggestions();
    }
  }

  updateSuggestionHover(hover: boolean): void {
    this.suggestionHover = hover;
  }

  async openSuggestions(): Promise<void> {
    this.suggestionHover = false;

    // Emit open event
    this.suggestionsOpen.emit();

    const isMobile = await firstValueFrom(
      this.isMobile$.pipe(untilDestroyed(this)),
    );

    const isOpen = await firstValueFrom(
      this.isOpen$.pipe(untilDestroyed(this)),
    );
    const oldTop = this.element.offsetTop;
    const caretCoordinates = this.autoCompleteRichText.getCaretCoordinates();

    this.renderer.setStyle(this.element, 'top', `${caretCoordinates.top}px`);
    if (isMobile) {
      this.renderer.setStyle(this.element, 'left', '0px');
    } else {
      this.renderer.setStyle(
        this.element,
        'left',
        `${caretCoordinates.left}px`,
      );
    }

    if (!isOpen) {
      if (isMobile) {
        this.renderer.setStyle(
          this.suggestionsCalloutContent.nativeElement,
          'display',
          'block',
        );
        this.renderer.setStyle(
          this.suggestionsCalloutContent.nativeElement,
          'width',
          `${this.autoCompleteRichTextElementRef.nativeElement.offsetWidth}px`,
        );
      }
      this.renderer.setStyle(
        this.element,
        'height',
        `${caretCoordinates.height}px`,
      );
      this.suggestionsCallout.open();
    } else if (oldTop !== caretCoordinates.top) {
      this.suggestionsCallout.updatePosition();
    }
  }

  async closeSuggestions(): Promise<void> {
    this.suggestionHover = false;

    // Invalidate suggestion list
    this.suggestionsSubject$.next(undefined);
    // Emit close event
    this.suggestionsClose.emit();
    this.suggestionsCallout?.close();
  }

  updateCaretPosition(offset: number): void {
    if (this.controlValueAccessorDirective) {
      this.controlValueAccessorDirective.setCursorPosition(offset);
    } else if (this.textareaComponent) {
      this.textareaComponent.controlValueAccessorDirective?.setCursorPosition(
        offset,
      );
    }
  }

  handlePasteEvent(pasteObject: PasteObject): void {
    this.selectionAfterPaste = this.autoCompleteRichText.getSelection();

    let updatedSegments = pasteObject.objects;
    if (updatedSegments) {
      const firstObject = updatedSegments[0];
      if (firstObject.completion) {
        // The firstObject may not starts at the beginning.
        // It finds the position of the first occurence of the string
        const adjustment = pasteObject.text.indexOf(firstObject.completion) - 1;

        // Recalculate the copied entities position
        for (let i = 0; i < updatedSegments.length; i++) {
          const copiedObject = { ...updatedSegments[i] };
          let calculatedOffset = this.selectionAfterPaste.left + adjustment;
          if (i > 0) {
            // Calculates the distance from the offset of thefirstObject
            // and adds to the calculatedOffset
            calculatedOffset += copiedObject.offset - firstObject.offset;
          }
          const ret = updatedSegments.slice(0);
          ret[i] = {
            referenceId: copiedObject.referenceId,
            referenceType: copiedObject.referenceType,
            completion: copiedObject.completion,
            offset: calculatedOffset,
          };
          updatedSegments = ret;
        }
      }
    }

    // Filter out suggestions that were affected by this paste operation
    if (this.selectionAfterPaste) {
      this.filterNonOverlappingSuggestions({
        startPos: this.selectionAfterPaste.left,
        endPos: this.selectionAfterPaste.right,
      });
    }

    // Calculate the effective change in length for paste
    const deltaLength =
      pasteObject.text.length -
      (this.selectionAfterPaste.right - this.selectionAfterPaste.left);

    // Adjust offsets for suggestions after the paste position
    this.adjustSuggestionOffsets({
      shiftPoint: this.selectionAfterPaste.right, // Start adjusting from the end of selection
      shiftDelta: deltaLength, // Shift by the net change in length
      boundaryCheck: this.selectionAfterPaste.left, // Use left-boundary as the edge case check
    });

    if (updatedSegments) {
      this.addHighlightedTextSegments(updatedSegments);
    }

    // Signal that the upcoming control change is programmatic
    this.programmaticChangeSubject$.next(true);
  }

  /**
   * Insert the selected TextSegment via FormControl and
   * update the caret position after the text has been
   * inserted into the native element.
   *
   * @param highlightedSegment
   */
  private insertHighlightedSegment(
    highlightedSegment: AutoCompleteObject,
  ): void {
    if (!highlightedSegment.completion) {
      return;
    }

    this.addHighlightedTextSegments([highlightedSegment]);
    const caretPosition = this.autoCompleteRichText.getSelectionLeftPosition();

    const prevValue = this.control.value;
    const head = prevValue.substring(0, highlightedSegment.offset + 1);
    const tail = prevValue.substring(caretPosition);
    const newValue = `${head}${highlightedSegment.completion} ${tail}`;

    // Calculate the length added by the new segment and the space
    const addedLength = highlightedSegment.completion.length + 1;

    // Adjust offsets of subsequent suggestions BEFORE setting the value
    this.adjustSuggestionOffsets({
      shiftPoint: caretPosition, // Start adjusting from the insertion point
      shiftDelta: addedLength, // Shift by the added length
      skipSuggestion: highlightedSegment, // Skip adjusting the suggestion being inserted
    });

    this.updateCaretPosition(
      caretPosition + highlightedSegment.completion.length + 1,
    );

    // T237883: For Mobile IOS, Updating caret via ControlValueAccessorDirective by calling this.updateCaretPosition
    // was not updating this.acceptedNode.selectionStart, which we use inside of tagging.directive collectTags call.
    // Therefore, update value of this.acceptedNode.selectionStart again by manually here

    this.autoCompleteRichText.setSelectionStart(
      caretPosition + highlightedSegment.completion.length + 1,
    );

    // Signal that the upcoming control change is programmatic
    this.programmaticChangeSubject$.next(true);
    this.control.setValue(newValue);
  }

  /**
   * Store highlighted text segments
   *
   * @param highlightedSegments
   */
  private addHighlightedTextSegments(
    highlightedSegments: readonly AutoCompleteObject[],
  ): void {
    if (!highlightedSegments || highlightedSegments.length === 0) {
      return;
    }

    // Filter out duplicates
    const existingSuggestionKeys = new Set<string>(
      this.selectedSuggestions.map(
        s => `${s.offset}-${s.referenceId}-${s.referenceType}`,
      ),
    );

    const newUniqueSegments = highlightedSegments.filter(hs => {
      if (!hs.completion) {
        return false;
      }
      const key = `${hs.offset}-${hs.referenceId}-${hs.referenceType}`;
      return !existingSuggestionKeys.has(key);
    });

    if (newUniqueSegments.length === 0) {
      return;
    }

    this.selectedSuggestions = [
      ...this.selectedSuggestions,
      ...newUniqueSegments,
    ].sort((a, b) => a.offset - b.offset);
  }

  private get element(): HTMLElement {
    return this.suggestionsTrigger.nativeElement;
  }

  private isMobileViewport(): Observable<boolean> {
    if (!isPlatformBrowser(this.platformId)) {
      return of(false);
    }

    return this.screenSize.isMobile();
  }

  /**
   * Filters suggestions that overlap with the specified range.
   * Used when content is edited to remove suggestions that should no longer exist.
   *
   * @param params Parameters for filtering suggestions
   */
  private filterNonOverlappingSuggestions({
    startPos,
    endPos,
  }: FilterSuggestionsParams): void {
    // If start and end positions are the same, this is likely a cursor position event with no changes
    // Or if end position is less than start position, this is invalid, so just return
    if (startPos >= endPos) {
      return;
    }

    this.selectedSuggestions = this.selectedSuggestions.filter(suggestion => {
      if (!suggestion.completion) {
        return false;
      }

      const { length } = suggestion.completion;
      const startOffset = suggestion.offset;
      const endOffset = startOffset + length + 1;

      // Special case: When inserting a single character at the beginning of a suggestion
      // (like typing @ in front of @mention), we should keep it
      const isInsertingAtStart =
        startPos === startOffset && endPos - startPos === 1;

      // Normal case: Keep a suggestion if it's completely outside the edit range
      return (
        endOffset <= startPos || startOffset >= endPos || isInsertingAtStart
      );
    });
  }

  /**
   * Adjusts suggestion offsets based on text changes and updates this.selectedSuggestions.
   *
   * @param params The parameters for offset adjustment
   */
  private adjustSuggestionOffsets({
    shiftPoint,
    shiftDelta,
    boundaryCheck,
    skipSuggestion,
  }: AdjustSuggestionOffsetsParams): void {
    if (shiftDelta === 0) {
      return; // No adjustment needed
    }

    this.selectedSuggestions = this.selectedSuggestions
      .map(suggestion => {
        // Skip the suggestion being inserted (if provided)
        if (skipSuggestion && suggestion === skipSuggestion) {
          return suggestion;
        }

        // For paste operations, check against the left boundary
        if (boundaryCheck !== undefined && suggestion.offset < boundaryCheck) {
          return suggestion;
        }

        // Adjust suggestions at or after the shift point
        if (suggestion.offset >= shiftPoint) {
          return {
            ...suggestion,
            offset: suggestion.offset + shiftDelta,
          };
        }

        return suggestion;
      })
      .sort((a, b) => a.offset - b.offset);
  }
}
